import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { money, today } from '../../utils/format';
import { ErrorNotice } from '../../components/ui';
import { DateInput, Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { CustomerSelect } from '../pos/CustomerSelect';

interface Scheme {
  id: string;
  name: string;
  scheme_type: 'AMOUNT' | 'WEIGHT';
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  instalments: number;
  instalment_amount: string;
  flexible: boolean;
  min_amount: string;
  max_amount: string | null;
  bonus_mode: string;
  bonus_value: string;
  purity_code: string | null;
  status: string;
}

const PAY_MODES = ['CASH', 'UPI', 'CARD', 'BANK', 'CHEQUE'] as const;

function addPeriod(start: string, period: string, steps: number): string {
  const d = new Date(`${start}T00:00:00Z`);
  if (period === 'DAILY') d.setUTCDate(d.getUTCDate() + steps);
  else if (period === 'WEEKLY') d.setUTCDate(d.getUTCDate() + steps * 7);
  else {
    const day = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + steps);
    d.setUTCDate(Math.min(day, new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()));
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Enrolling a customer in a scheme.
 *
 * Everything the registration slip asks for, and the counter's usual next
 * step — taking the first instalment on the spot — in the same save, so the
 * customer leaves with a passbook and a receipt. The maturity date and what
 * they will get at the end are shown as the form is filled in, because that is
 * the question every customer asks before signing.
 */
export function EnrolDrawer({ onClose, onEnrolled }: { onClose: () => void; onEnrolled: (id: string) => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();
  const [error, setError] = useState<unknown>(null);
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [f, setF] = useState({
    schemeId: '',
    branchId: '',
    joinDate: today(),
    amount: '',
    introducerName: '',
    nomineeName: '',
    nomineeRelation: '',
    remarks: '',
    payNow: true,
    payMode: 'CASH',
    referenceNo: '',
  });

  const schemes = useQuery({ queryKey: ['chit-schemes'], queryFn: () => api.get<{ data: Scheme[] }>('/chit/schemes') });
  const open = (schemes.data?.data ?? []).filter((s) => s.status === 'ACTIVE');
  const scheme = open.find((s) => s.id === f.schemeId);

  const fixed = scheme ? Number(scheme.instalment_amount) : 0;
  const amount = fixed > 0 ? fixed : Number(f.amount || 0);
  const outOfRange =
    scheme && amount > 0 && (amount < Number(scheme.min_amount) || (scheme.max_amount !== null && amount > Number(scheme.max_amount)));

  const preview = useMemo(() => {
    if (!scheme) return null;
    const maturity = addPeriod(f.joinDate, scheme.period, scheme.instalments - 1);
    const saved = amount * scheme.instalments;
    const bonus =
      scheme.bonus_mode === 'PERCENT'
        ? (saved * Number(scheme.bonus_value)) / 100
        : scheme.bonus_mode === 'ONE_INSTALMENT'
          ? amount
          : scheme.bonus_mode === 'FIXED'
            ? Number(scheme.bonus_value)
            : 0;
    return { maturity, saved, bonus };
  }, [scheme, f.joinDate, amount]);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; chitNo: string; receiptNo?: string }>('/chit/accounts', {
        schemeId: f.schemeId,
        customerId: customer!.id,
        branchId: f.branchId || defaultBranchId,
        joinDate: f.joinDate,
        ...(fixed > 0 ? {} : { instalmentAmount: Number(f.amount).toFixed(2) }),
        ...(f.introducerName.trim() ? { introducerName: f.introducerName.trim() } : {}),
        ...(f.nomineeName.trim() ? { nomineeName: f.nomineeName.trim() } : {}),
        ...(f.nomineeRelation.trim() ? { nomineeRelation: f.nomineeRelation.trim() } : {}),
        ...(f.remarks.trim() ? { remarks: f.remarks.trim() } : {}),
        ...(f.payNow && amount > 0
          ? { firstPayment: { payMode: f.payMode, ...(f.referenceNo.trim() ? { referenceNo: f.referenceNo.trim() } : {}) } }
          : {}),
      }),
    onSuccess: (r) => {
      toast(`${customer!.name} enrolled as ${r.chitNo}`, r.receiptNo ? `First instalment received — ${r.receiptNo}` : 'No payment taken yet');
      void client.invalidateQueries({ queryKey: ['chit-accounts'] });
      void client.invalidateQueries({ queryKey: ['chit-overview'] });
      onEnrolled(r.id);
      onClose();
    },
    onError: setError,
  });

  const ready = !!scheme && !!customer && amount > 0 && !outOfRange;

  return (
    <Drawer
      title="Enrol a customer"
      subtitle="Register a customer in a savings scheme"
      onClose={onClose}
      footer={
        <button className="btn-primary w-full" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending
            ? 'Enrolling…'
            : f.payNow && amount > 0
              ? `Enrol and take ${money(amount.toFixed(2))}`
              : 'Enrol'}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => e.preventDefault()} className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Scheme</h3>
          <Field label="Scheme" required>
            <Select
              value={f.schemeId}
              onChange={(v) => setF({ ...f, schemeId: v, amount: '' })}
              options={open.map((s) => ({
                value: s.id,
                label: `${s.name} — ${s.instalments} × ${Number(s.instalment_amount) > 0 ? money(s.instalment_amount) : 'your amount'}`,
              }))}
              placeholder={open.length === 0 ? 'No schemes yet — create one first' : 'Choose a scheme'}
            />
          </Field>
          {scheme && fixed === 0 ? (
            <Field
              label="Amount each time"
              required
              hint={`Between ${money(scheme.min_amount)} and ${scheme.max_amount ? money(scheme.max_amount) : 'any amount'}`}
              error={outOfRange ? 'Outside what this scheme allows.' : null}
            >
              <NumberInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} decimals={2} placeholder="2000" />
            </Field>
          ) : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Customer</h3>
          <CustomerSelect value={customer} onChange={setCustomer} />
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Instalments</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of joining" required>
              <DateInput value={f.joinDate} onChange={(v) => setF({ ...f, joinDate: v })} />
            </Field>
            <Field label="Matures on">
              <input className="field num bg-silver-50" readOnly value={preview?.maturity ?? '—'} />
            </Field>
          </div>
          {branches.length > 1 ? (
            <Field label="Branch">
              <Select
                value={f.branchId || defaultBranchId || ''}
                onChange={(v) => setF({ ...f, branchId: v })}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
          ) : null}

          {preview && amount > 0 ? (
            <div className="rounded-lg border border-gold-300 bg-gold-50 p-3 text-sm">
              <div className="flex justify-between">
                <span>
                  {scheme!.instalments} × {money(amount.toFixed(2))}
                </span>
                <span className="num">{money(preview.saved.toFixed(2))}</span>
              </div>
              <div className="flex justify-between text-success">
                <span>Shop’s bonus</span>
                <span className="num">+ {money(preview.bonus.toFixed(2))}</span>
              </div>
              <div className="mt-1 flex justify-between border-t border-gold-300 pt-1 font-semibold">
                <span>{scheme!.scheme_type === 'WEIGHT' ? 'Saved as gold, plus bonus' : 'To spend at maturity'}</span>
                <span className="num">{money((preview.saved + preview.bonus).toFixed(2))}</span>
              </div>
              {scheme!.scheme_type === 'WEIGHT' ? (
                <p className="mt-1 text-2xs text-ink-soft">
                  Each instalment buys {scheme!.purity_code} gold at that day’s rate, so the customer keeps the grams even if the price rises.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Other details</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nominee">
              <TextInput value={f.nomineeName} onChange={(v) => setF({ ...f, nomineeName: v })} maxLength={120} />
            </Field>
            <Field label="Relation">
              <TextInput value={f.nomineeRelation} onChange={(v) => setF({ ...f, nomineeRelation: v })} maxLength={60} placeholder="Son, wife…" />
            </Field>
          </div>
          <Field label="Introduced by">
            <TextInput value={f.introducerName} onChange={(v) => setF({ ...f, introducerName: v })} maxLength={120} />
          </Field>
          <Field label="Remarks">
            <TextInput value={f.remarks} onChange={(v) => setF({ ...f, remarks: v })} maxLength={500} />
          </Field>
        </section>

        {amount > 0 ? (
          <section className="space-y-3 rounded-lg border border-silver-100 p-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={f.payNow} onChange={(e) => setF({ ...f, payNow: e.target.checked })} />
              Take the first instalment now
            </label>
            {f.payNow ? (
              <>
                <div className="flex flex-wrap gap-2">
                  {PAY_MODES.map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() => setF({ ...f, payMode: m })}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        f.payMode === m ? 'border-gold-500 bg-gold-50 font-medium text-gold-700' : 'border-silver-100 text-ink-soft'
                      }`}
                    >
                      {m === 'BANK' ? 'Bank' : m.charAt(0) + m.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                {f.payMode !== 'CASH' ? (
                  <Field label={f.payMode === 'CHEQUE' ? 'Cheque number' : 'Reference'}>
                    <TextInput value={f.referenceNo} onChange={(v) => setF({ ...f, referenceNo: v })} maxLength={64} />
                  </Field>
                ) : null}
              </>
            ) : null}
          </section>
        ) : null}
      </form>
    </Drawer>
  );
}
